import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import Bugsnag from '@bugsnag/js';

// https://codesandbox.io/s/infallible-snow-0z3qn?file=/src/hooks/useNotifications.ts
const useFavorites = ({
    saveQuery,
    removeQuery,
    readItemQuery,
    type,
    typePlural,
    saveMutationName,
    initialIsFavorite,
}) => {
    const [isLoadingFavorite, setIsLoadingFavorite] = useState(false);
    const [isFavorite, setIsFavorite] = useState(initialIsFavorite);

    const [saveMutate] = useMutation(saveQuery, {
        onCompleted: res => onComplete(),
        onError: e => onErrorSave(e),
    });

    const [removeMutate] = useMutation(removeQuery, {
        onCompleted: () => onComplete(),
        onError: e => onErrorRemove(e),
    });

    const onComplete = res => {
        setIsFavorite(!isFavorite);
        setIsLoadingFavorite(false);
    };

    const onErrorSave = e => {
        Bugsnag.notify(e, function(event) {
            event.context = 'Saving favorite';
        });
        setIsLoadingFavorite(false);
    };

    const onErrorRemove = e => {
        Bugsnag.notify(e, function(event) {
            event.context = 'Removing favorite';
        });
        setIsLoadingFavorite(false);
    };

    const save = React.useCallback(
        async recordId => {
            setIsLoadingFavorite(true);

            await saveMutate({
                variables: { recordId },
                update: (cache, { data }) => {
                    const existingItems = cache.readQuery({ query: readItemQuery });
                    // e.g. data.savePublication.publication
                    const newItem = data[saveMutationName][type];

                    // e.g. items.publications
                    // filter out nulls
                    const nonNullItems = existingItems[typePlural].filter(c => !!c);
                    const existingIds = nonNullItems.map(c => c.recordId);
                    // item is not removed from cache so it isn't removed from ui (My ICSC saved items)
                    // so don't add it if it's already in the cache.
                    if (!existingIds.includes(newItem.recordId)) {
                        nonNullItems.push(newItem);
                    }

                    cache.writeQuery({
                        query: readItemQuery,
                        data: {
                            [typePlural]: nonNullItems,
                        },
                    });
                },
            });
        },
        [readItemQuery, saveMutate, saveMutationName, type, typePlural]
    );

    const remove = React.useCallback(
        async recordId => {
            setIsLoadingFavorite(true);
            await removeMutate({
                variables: { recordId },
                update: (cache, { data }) => {
                    const existingItems = cache.readQuery({ query: readItemQuery });
                    const nonNullItems = existingItems[typePlural].filter(item => !!item);
                    const newItems = nonNullItems.filter(event => recordId !== event.recordId);

                    cache.writeQuery({
                        query: readItemQuery,
                        data: {
                            [typePlural]: newItems,
                        },
                    });
                },
            });
        },
        [readItemQuery, removeMutate, typePlural]
    );

    const setManually = value => {
        setIsFavorite(value);
    };

    return {
        isLoadingFavorite,
        isFavorite,
        saveFavorite: save,
        removeFavorite: remove,
        setIsFavoriteManually: setManually,
    };
};

export default useFavorites;
